import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Modal,
  Typography,
  useTheme,
} from "@mui/material";
import { CustomTypography } from "../custom-typography/custom-typography";
import { CancelIcon } from "../../assets/icon";
import {
  AssessmentTeamItem,
  getAssessmentsTeam,
} from "../../axios/quiz";
import useUserStore from "../../stores/userStore";
import { isAxiosNetworkError } from "../../utils/internetConnection";
import { AssessmentManagerDashboard } from "../quiz-pop-up/assessment-manager-dashboard/assessment-manager-dashboard";
import {
  contentStyles,
  errorActionsStyles,
  fullWidthGridItemStyles,
  headerStyles,
  itemHeaderRowStyles,
  itemMetaStyles,
  itemStyles,
  itemTitleStyles,
  itemsGridStyles,
  loadingGridItemStyles,
  modalBoxStyles,
  retryButtonStyles,
  statusChipStyles,
  type TeamAssessmentStatus,
} from "./styles";

interface TeamAssessmentsModalProps {
  open: boolean;
  onClose: () => void;
}

const LIST_POLL_MS = 20_000;
const OFFLINE_LIST_MESSAGE =
  "No internet connection. Reconnect and tap Retry.";
const LOAD_LIST_ERROR_MESSAGE = "Failed to load team contests.";

const formatDateTime = (value: string | null): string | null => {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatSummaryLine = (item: AssessmentTeamItem): string | null => {
  const summary = item.summary;
  if (!summary) {
    return null;
  }
  return `${summary.completed}/${summary.invited} completed · ${summary.in_progress} in progress`;
};

const resolveTeamAssessmentStatus = (
  item: AssessmentTeamItem,
): TeamAssessmentStatus => {
  if (item.is_finished) {
    return "finished";
  }
  if (item.is_live) {
    return "live";
  }
  if (item.is_scheduled) {
    return "scheduled";
  }
  return "finished";
};

const statusChipLabel = (status: TeamAssessmentStatus): string => {
  if (status === "live") return "Live";
  if (status === "scheduled") return "Scheduled";
  return "Finished";
};

export const TeamAssessmentsModal: React.FC<TeamAssessmentsModalProps> = ({
  open,
  onClose,
}) => {
  const getFocusDisplayName = useUserStore((state) => state.getFocusDisplayName);
  const theme = useTheme();
  const [items, setItems] = useState<AssessmentTeamItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedAssessment, setSelectedAssessment] =
    useState<AssessmentTeamItem | null>(null);
  const hasLoadedOnceRef = useRef(false);

  const fetchTeamAssessments = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent === true;
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      if (!navigator.onLine) {
        if (!silent) {
          setError(OFFLINE_LIST_MESSAGE);
          setItems([]);
        }
        return;
      }
      const response = await getAssessmentsTeam();
      setItems(response.items ?? []);
      setError(null);
      hasLoadedOnceRef.current = true;
    } catch (fetchError) {
      // Keep previous items on silent poll failures so the list does not flash empty.
      if (!silent) {
        const offline =
          !navigator.onLine || isAxiosNetworkError(fetchError);
        setError(offline ? OFFLINE_LIST_MESSAGE : LOAD_LIST_ERROR_MESSAGE);
        setItems([]);
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!open) {
      setSelectedAssessment(null);
      setError(null);
      hasLoadedOnceRef.current = false;
      return;
    }
    void fetchTeamAssessments();
  }, [open, fetchTeamAssessments]);

  // Refresh the list while the list modal stays open (pause while dashboard is open).
  useEffect(() => {
    if (!open || selectedAssessment) {
      return;
    }

    const pollId = window.setInterval(() => {
      void fetchTeamAssessments({ silent: true });
    }, LIST_POLL_MS);

    return () => {
      window.clearInterval(pollId);
    };
  }, [open, selectedAssessment, fetchTeamAssessments]);

  // Refresh once when returning from the dashboard to the list.
  useEffect(() => {
    if (!open || selectedAssessment || !hasLoadedOnceRef.current) {
      return;
    }
    void fetchTeamAssessments({ silent: true });
  }, [open, selectedAssessment, fetchTeamAssessments]);

  const handleClose = () => {
    setSelectedAssessment(null);
    onClose();
  };

  const handleSelectItem = (item: AssessmentTeamItem) => {
    setSelectedAssessment(item);
  };

  const handleRetry = () => {
    void fetchTeamAssessments();
  };

  const showInitialLoading = loading && items.length === 0;
  const showErrorState = !loading && Boolean(error) && items.length === 0;

  return (
    <>
      <Modal open={open && !selectedAssessment} onClose={handleClose}>
        <Box sx={modalBoxStyles}>
          <Box sx={headerStyles}>
            <CustomTypography fontSize={20} fontWeight={600} color="#FFFFFF">
              My Team Statistics
            </CustomTypography>
            <CancelIcon
              color="#FFFFFF"
              onClick={handleClose}
              style={{ cursor: "pointer" }}
            />
          </Box>
          <Box sx={contentStyles}>
            <Box sx={itemsGridStyles}>
              {showInitialLoading && (
                <Box sx={loadingGridItemStyles}>
                  <CircularProgress size={28} />
                </Box>
              )}
              {showErrorState && (
                <Box sx={fullWidthGridItemStyles}>
                  <Box sx={errorActionsStyles}>
                    <Typography sx={itemMetaStyles}>{error}</Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleRetry}
                      disabled={loading}
                      sx={retryButtonStyles}
                    >
                      Retry
                    </Button>
                  </Box>
                </Box>
              )}
              {!showInitialLoading &&
                !showErrorState &&
                items.length === 0 && (
                <Box sx={fullWidthGridItemStyles}>
                  <Typography sx={itemMetaStyles}>
                    No team contests yet.
                  </Typography>
                </Box>
              )}
              {!showErrorState &&
                items.map((item) => {
                  const status = resolveTeamAssessmentStatus(item);
                  const dateValue =
                    status === "finished"
                      ? item.finished_at ?? item.updated_at
                      : item.updated_at;
                  const dateLabel = formatDateTime(dateValue);
                  const summaryLine = formatSummaryLine(item);

                  return (
                    <Box
                      key={item.assessment_id}
                      sx={itemStyles}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleSelectItem(item)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          handleSelectItem(item);
                        }
                      }}
                    >
                      <Box sx={itemHeaderRowStyles}>
                        <Typography
                          sx={itemTitleStyles}
                          title={item.title || "Contest"}
                          noWrap
                        >
                          {item.title || "Contest"}
                        </Typography>
                        <Chip
                          size="small"
                          label={statusChipLabel(status)}
                          sx={{
                            ...statusChipStyles(theme, status),
                            flexShrink: 0,
                          }}
                        />
                      </Box>
                      {item.focus ? (
                        <Typography sx={itemMetaStyles}>
                          {getFocusDisplayName(item.focus)}
                        </Typography>
                      ) : null}
                      {dateLabel ? (
                        <Typography sx={itemMetaStyles}>
                          {status === "live"
                            ? "Updated"
                            : status === "scheduled"
                              ? "Scheduled"
                              : "Finished"}
                          : {dateLabel}
                        </Typography>
                      ) : null}
                      {summaryLine ? (
                        <Typography sx={itemMetaStyles}>{summaryLine}</Typography>
                      ) : null}
                    </Box>
                  );
                })}
            </Box>
          </Box>
        </Box>
      </Modal>

      <AssessmentManagerDashboard
        open={Boolean(selectedAssessment)}
        onClose={() => setSelectedAssessment(null)}
        assessmentId={selectedAssessment?.assessment_id ?? null}
        questionIndex={0}
        currentQuestionLabel="Q1"
        dashboardType="overview"
        selectedFocus={selectedAssessment?.focus ?? ""}
      />
    </>
  );
};

export default TeamAssessmentsModal;
